import * as Sentry from '@sentry/browser'
import cookies from 'js-cookie'
import { getRequest } from 'src/utils/request-client'
import Storage from 'src/utils/storage'
import BrokerService from 'src/utils/broker-service'
import idbKeyval from 'idb-keyval'

export function isLocal() {
  return process.env.REACT_APP_ENV === 'development'
}

export function isNodeLocal() {
  return process.env.NODE_ENV === 'development'
}

export function getStaticUrl() {
  if (process.env.REACT_APP_DEV_HTTPS !== 'true' && process.env.REACT_APP_STATIC_URL_HTTP) {
    return process.env.REACT_APP_STATIC_URL_HTTP
  }

  return process.env.REACT_APP_STATIC_URL
}

/**
 * Convert "api base" variable to "api src". Used
 *
 * TODO: PAT-2318 Check if this should be a new variable, instead of converting it
 *
 * @return {string}
 */
export function getApiSrcImagesUrl() {
  try {
    return getApiAddress()
      .replace('://', '://src.')
      .replace('/api/kiosk', '/images/kiosk')
  } catch (e) {
    return ''
  }
}

export function setThemeColors(colors) {
  const doc = getKioskDocument()
  doc.documentElement.style.setProperty('--chakra-colors-primary', colors.brandColor || '#A3AFB7')
  doc.documentElement.style.setProperty(
    '--chakra-colors-secondary',
    colors.backgroundFontColor || '#A3AFB7',
  )
  doc.documentElement.style.setProperty('--chakra-colors-accent', colors.actionColor || '#A3AFB7')
  doc.documentElement.style.setProperty(
    '--chakra-colors-header-font',
    colors.brandContrastColor || '#FFFFFF',
  )
  doc.documentElement.style.setProperty(
    '--chakra-colors-card',
    colors.cardBackgroundColor || '#FFFFFF',
  )
  doc.documentElement.style.setProperty(
    '--chakra-colors-background',
    colors.backgroundColor || '#FFFFFF',
  )
  doc.documentElement.style.setProperty(
    '--chakra-colors-card-font',
    colors.cardFontColor || '#A3AFB7',
  )
  // 12 is added for opacity to hex, the same as 0.07
  doc.documentElement.style.setProperty(
    '--chakra-colors-icon-container',
    colors.cardFontColor + '12',
  )
  doc.documentElement.style.setProperty(
    '--chakra-colors-modal-icon-container',
    colors.actionColor + '12',
  )
  doc.documentElement.style.setProperty('--chakra-colors-menu-item', colors.actionColor + '1A')

  doc.documentElement.style.setProperty(
    '--chakra-colors-card-column-active',
    colors.actionColor + '08',
  )
}

/**
 * Get prefix for naming our cookies
 *
 * @return {string}
 */
function getCookiePrefix() {
  const defaultResponse = 'pw-default_'

  try {
    const broker = BrokerService.getBroker()
    if (!broker) {
      return defaultResponse
    }

    return `pw-${broker}_`
  } catch (e) {
    return defaultResponse
  }
}

/**
 * Set cookie with the broker name so it doesn't mix with other kiosk instances.
 *
 * @param {string} name
 * @param {string} value
 * @param {number} expiresInMinutes After how many minutes should this cookie expire
 */
export function setBrokerCookie(name, value, expiresInMinutes = 0) {
  try {
    if (expiresInMinutes <= 0) {
      // Default to 10 years if not set
      expiresInMinutes = 60 * 24 * 365 * 10
    }

    cookies.set(`${getCookiePrefix()}${name}`, value, {
      // Value in days (needs to have value to prevent clearing when standalone app is terminated)
      expires: (1 / 24 / 60) * expiresInMinutes,
      Secure: !isLocal(),
      // SameSite: 'Strict',
      SameSite: isNodeLocal() ? 'Lax' : 'None',
    })
  } catch (e) {
    logError('Set ' + name + ' cookie', e)
  }
}

/**
 * Get cookie with the broker name so it doesn't mix with other kiosk instances.
 *
 * @param name
 * @returns {string|null}
 */
export function getBrokerCookie(name) {
  try {
    return cookies.get(`${getCookiePrefix()}${name}`) || null
  } catch (e) {
    logError('Get ' + name + ' cookie', e)
    return null
  }
}

/**
 * Clears cookie with the broker name so it doesn't mix with other kiosk instances.
 *
 * @param name
 */
export function removeBrokerCookie(name) {
  try {
    cookies.remove(`${getCookiePrefix()}${name}`)
  } catch (e) {
    logError('Remove ' + name + ' cookie', e)
  }
}

export function parseGoogleResponse(components) {
  try {
    return components.reduce((result, component) => {
      const data = { ...result }
      component.types.forEach(type => {
        if (type === 'street_number') {
          data.streetNumber = component.long_name
        }
        if (type === 'route') {
          data.streetName = component.long_name
        }
        if (type === 'locality') {
          data.city = component.long_name
        }
        if (type === 'administrative_area_level_1' || type === 'administrative_area_level_2') {
          data.province = component.long_name
        }
        if (type === 'country') {
          data.country = component.short_name
        }
        if (type === 'postal_code') {
          data.zipCode = component.long_name
        }
      })
      return data
    }, {})
  } catch (e) {
    logError('Parse google response error', e)
    return {}
  }
}

/**
 * Count and returns number of decimals in provided number.
 *
 * @param {string|number} value
 * @return {int} Number of decimals
 */
export function countDecimals(value) {
  try {
    return value.toString().split('.')[1].length
  } catch (e) {
    return 0
  }
}

export function roundDecimals(value, toDecimals = 2) {
  try {
    return parseFloat(value).toFixed(toDecimals)
  } catch (e) {
    return value
  }
}

/**
 * General currency formatting options.
 * Currently used with 'react-number-format' package.
 *
 * @param {number} decimalPlaces
 * @return {{
 *   thousandSeparator: boolean,
 *   decimalScale: number,
 *   prefix: string
 * }}
 */
export function currencySettings(decimalPlaces = 2) {
  return {
    thousandSeparator: true,
    decimalScale: decimalPlaces,
    prefix: '$',
  }
}

/**
 * Format currency
 *
 * @param {number} value
 * @param {number} decimalPlaces
 * @return {string|number}
 */
export function formatCurrency(value, decimalPlaces = 2) {
  try {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value)
  } catch (e) {
    return value
  }
}

export function camelCaseToSenteseCase(string) {
  return string.replace(/([A-Z]+)*([A-Z][a-z])/g, '$1 $2')
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * Just tries to get host part of URL,
 * but returns whatever it receives if it fails.
 *
 * @param {string} websiteUrl
 * @return {string}
 */
export function getHostFromWebsite(websiteUrl) {
  try {
    return new URL(websiteUrl).host
  } catch (e) {
    return websiteUrl
  }
}

export function truncateSentense(sentence, numOfSentese = 5) {
  let sentenseArray = sentence.split(' ')
  if (sentenseArray.length > numOfSentese) {
    return sentenseArray.slice(0, numOfSentese).join(' ') + '...'
  }
  return sentence
}

export function truncate(string, truncateLength = 100) {
  if (string.length > truncateLength) {
    return string.substring(0, truncateLength) + '...'
  } else {
    return string
  }
}

export function addOrRemoveArray(arr, item) {
  return arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]
}

export function addUniqueArray(arr, item) {
  return arr.includes(item) ? arr : [...arr, item]
}

export function isSameArray(arr1, arr2) {
  try {
    // If arrays don't even have same number of items, they are definitely not the same
    if (arr1.length !== arr2.length) {
      return false
    }

    const arr1Sorted = arr1.slice().sort()
    const arr2Sorted = arr2.slice().sort()

    return arr1Sorted.every((value, index) => value === arr2Sorted[index])
  } catch (e) {
    return false
  }
}

export function getMimeType(mimeType) {
  if (!mimeType) {
    return 'text'
  }

  if (mimeType === 'image' || mimeType === 'file') {
    return mimeType
  }
  if (mimeType.indexOf('image') > -1) {
    return 'image'
  } else if (mimeType.indexOf('application') > -1) {
    return 'file'
  } else {
    return mimeType
  }
}

/**
 * Gets API URL from config file based on current environment (production, staging, etc.)
 *
 * @param {bool} isMock
 * @return {string} API URL
 */
export function getApiAddress(isMock = false) {
  // TODO: [Desktop] Remove "mock" complications

  if (isMock) {
    if (isLocal()) {
      return process.env.REACT_APP_API_BASE_MOCK_DEVELOPMENT || ''
    } else {
      return process.env.REACT_APP_API_BASE_MOCK_TEST || ''
    }
  } else {
    return process.env.REACT_APP_API_BASE || ''
  }
}

export function groupBy(list, keyGetter) {
  const map = new Map()
  list.forEach(item => {
    const key = keyGetter(item)
    const collection = map.get(key)
    if (!collection) {
      map.set(key, [item])
    } else {
      collection.push(item)
    }
  })
  return map
}

/**
 * Generate random string.
 *
 * @return {string}
 */
export function generateId() {
  return Math.random()
    .toString(36)
    .substring(7)
}

/**
 * Return widget popup timeout in minutes. After which we can popup widget again.
 *
 * @returns {number}
 */
export function getWidgetPopupTimeout() {
  if (hasLocalStorage() && localStorage.getItem('widgetPopupTimeout')) {
    return parseInt(localStorage.getItem('widgetPopupTimeout'))
  }

  // 12 hours for widget popup timeout by default
  return 60 * 12
}

/**
 * Stores timestamp when widget will be allowed to popup again.
 *
 * @returns {void}
 */
export function updateWidgetPopupTimestamp() {
  const timestamp = new Date().getTime() + getWidgetPopupTimeout() * 60 * 1000
  Storage.setItem('allowWidgetPopupTimestamp', timestamp)
}

/**
 * Checks if widget is allowed to popup based on saved on storage (localStorage).
 *
 * @returns {boolean}
 */
export function isWidgetPopupAllowed() {
  try {
    const timestamp = Storage.getItem('allowWidgetPopupTimestamp')

    // Allowed to popup if not yet set in storage (popup not yet closed)
    if (!timestamp) {
      return true
    }

    // If current time reaches timestamp set in storage
    return timeSinceTimestamp(timestamp, 'minutes') >= 0
  } catch (e) {
    return true
  }
}

/**
 * Check if user is logged in.
 *
 * @param {object} user User from state (state.user.user)
 * @return {boolean}
 */
export function isLoggedIn(user) {
  try {
    return !!(!isEmpty(getLoginToken()) && !isEmpty(user))
  } catch (e) {
    return false
  }
}

/**
 * Return login timeout in minutes, after which cookie will expire and extend session popup will show up.
 *
 * @returns {number} Number in minutes
 */
export function getSessionTimeout() {
  if (hasLocalStorage() && localStorage.getItem('autoLogOutTimeout')) {
    return parseInt(localStorage.getItem('autoLogOutTimeout'))
  }

  // Keep login cookie for 10 years in local environment or for standalone app
  if (isLocal() || isNodeLocal() || isInStandaloneMode()) {
    return 60 * 24 * 365 * 10
  }

  // 15 minutes for logout timeout by default
  return 15
}

/**
 * For how long should we show "Extend session" popup to keep you logged in.
 *
 * Defined here because it's used on multiple places (for the UI and for the cookie).
 *
 * @return {number} Number in seconds
 */
export function getLoginExtendTimeout() {
  if (hasLocalStorage() && localStorage.getItem('extendAutoLogOutTimeout')) {
    return parseInt(localStorage.getItem('extendAutoLogOutTimeout'))
  }

  return 60
}

/**
 * How long can login or chat session exist (in a cookie) with additional extend period.
 *
 * @return {number} Number in minutes
 */
export function getSessionTimeoutExtended() {
  // Add additional time (1 minute + 3 seconds), to allow session to be extended from popup
  return getSessionTimeout() + getLoginExtendTimeout() / 60 + 3 / 60
}

/**
 * Figure out how long can chat data stay in the cookie based on existing messages and login state.
 *
 * @param {object} user Chat identity will expire only if user is not logged
 * @param {boolean} isChatEmpty Chat identity will expire only if chat is not empty (if there are some messages)
 * @return {number} Number in minutes
 */
export function getChatSessionTimeout(user = {}, isChatEmpty = true) {
  const isNotLoggedIn = isEmpty(user)

  // If not logged in and there are some messages in chat
  if (isNotLoggedIn && isChatEmpty === false) {
    // 1 hour by default
    return getLongerIdleTimeout()
  }

  // It shouldn't be longer than login session if logged in
  if (!isNotLoggedIn) {
    return getSessionTimeoutExtended()
  }

  return 60 * 24 * 365 * 10
}

/**
 * Stores new login token.
 *
 * @param {string} token
 * @returns {void}
 */
export function setLoginToken(token) {
  cookies.set(`${getCookiePrefix()}auth-token`, token, {
    // Expects value in days
    expires: (1 / 24 / 60) * getSessionTimeoutExtended(),
    Secure: !isLocal(),
    // SameSite: 'Strict',
    SameSite: isNodeLocal() ? 'Lax' : 'None',
  })

  try {
    // Store token in IndexedDB, so it can be accessible from Service Worker
    idbKeyval.set('is-user-logged-in', true)
  } catch (e) {
    logError('IndexedDb set token error', e)
  }
}

/**
 * Returns stored token.
 *
 * @returns {string|null} Stored token
 */
export function getLoginToken() {
  try {
    const identity = cookies.get(`${getCookiePrefix()}auth-token`) || null
    if (identity) {
      return identity
    }

    // Fallback for backward compatibility
    return cookies.get('pw-auth-token') || null
  } catch (e) {
    return null
  }
}

/**
 * Stores notification banner hidden.
 *
 * @param {boolean} hidden
 * @returns {void}
 */
export function setNotificationCookieHidden(hidden) {
  cookies.set(`${getCookiePrefix()}notification-banner-hidden`, hidden, {
    // Expects value in days
    expires: (1 / 24 / 60) * getSessionTimeoutExtended(),
    Secure: !isLocal(),
    // SameSite: 'Strict',
    SameSite: isNodeLocal() ? 'Lax' : 'None',
  })
}

/**
 * Returns notification banner hidden.
 *
 * @returns {boolean|null} Hidden banner
 */
export function getNotificationCookieHidden() {
  try {
    const identity = cookies.get(`${getCookiePrefix()}notification-banner-hidden`) || null
    if (identity) {
      return identity
    }

    // Fallback for backward compatibility
    return cookies.get('pw-notification-banner-hidden') || null
  } catch (e) {
    return null
  }
}

/**
 * Clears stored token.
 *
 * @returns {void}
 */
export function removeLoginToken() {
  cookies.remove(`${getCookiePrefix()}auth-token`)
  cookies.remove('pw-auth-token')

  try {
    // Clear token from IndexedDB as well
    idbKeyval.delete('is-user-logged-in')
  } catch (e) {
    logError('IndexedDb del token error', e)
  }
}

/**
 * Gets already stored token and sets it again, to extend login session.
 *
 * @returns {void}
 */
export function extendLoginToken() {
  const token = getLoginToken()
  if (token) {
    setLoginToken(token)
  }
}

/**
 * Get timeout after we should clear data.
 *
 * Currently used to clear chat for anonymous users.
 *
 * @returns {number} Value in minutes
 */
export function getLongerIdleTimeout() {
  if (hasLocalStorage() && localStorage.getItem('longerIdleTimeout')) {
    return parseInt(localStorage.getItem('longerIdleTimeout'))
  }

  // Set longed timeout to much longer (14 days) when used as standalone app (or local testing)
  if (isLocal() || isNodeLocal() || isInStandaloneMode()) {
    return 60 * 24 * 14
  }

  // 60 minutes by default
  return 60
}

/**
 * Returns stored chat identity.
 *
 * @returns {string|null} Stored chat identity
 */
export function getChatIdentity() {
  try {
    const identity = cookies.get(`${getCookiePrefix()}chat-identity`) || null
    if (identity) {
      return identity
    }

    // Fallback for backward compatibility
    return cookies.get('pathway-identity') || null
  } catch (e) {
    return null
  }
}

/**
 * Store chat identity.
 *
 * @param {string} identity
 * @param {object} user Chat identity will expire only if user is not logged
 * @param {boolean} isChatEmpty Chat identity will expire only if chat is not empty (if there are some messages)
 */
export function setChatIdentity(identity, user = {}, isChatEmpty = true) {
  cookies.set(`${getCookiePrefix()}chat-identity`, identity, {
    // Expects value in days
    expires: (1 / 24 / 60) * getChatSessionTimeout(user, isChatEmpty),
    Secure: !isLocal(),
    // SameSite: 'Strict',
    SameSite: isNodeLocal() ? 'Lax' : 'None',
  })
}

/**
 * Returns existing chat identity or generates new one and returns that.
 *
 * @param {object} user Chat identity will expire only if user is not logged
 * @param {boolean} isChatEmpty Chat identity will expire only if chat is not empty (if there are some messages)
 * @return {string}
 */
export function getOrCreateChatIdentity(user, isChatEmpty = true) {
  const identity = getChatIdentity()

  if (identity) {
    return identity
  }

  // Create new chat identity
  const newIdentity = `${generateId()}@${window.location.hostname}`

  setChatIdentity(newIdentity, user, isChatEmpty)

  return newIdentity
}

/**
 * Clears chat identity
 */
export function removeChatIdentity() {
  try {
    cookies.remove(`${getCookiePrefix()}chat-identity`)
    cookies.remove('pathway-identity')
  } catch (e) {
    logError('Remove chat identity cookie', e)
  }
}

/**
 * Gets already stored chat identity and sets it again, to extend the session.
 *
 * @param {object} user Chat identity will expire only if user is not logged
 * @param {boolean} isChatEmpty Chat identity will expire only if chat is not empty (if there are some messages)
 * @returns {void}
 */
export function updateChatIdentity(user = {}, isChatEmpty = true) {
  const identity = getChatIdentity()
  if (identity) {
    setChatIdentity(identity, user, isChatEmpty)
  }
}

/**
 * Set chat channel name to the cookie.
 *
 * @param {string} chatChannelName
 * @param {object} user Chat identity will expire only if user is not logged
 * @param {boolean} isChatEmpty Chat identity will expire only if chat is not empty (if there are some messages)
 * @return void
 */
export function setChatChannelName(chatChannelName, user = {}, isChatEmpty = true) {
  setBrokerCookie('chat-channel-name', chatChannelName, getChatSessionTimeout(user, isChatEmpty))
}

/**
 * Get current chat channel name if it exists in the cookie.
 *
 * @return {string|null}
 */
export function getChatChannelName() {
  return getBrokerCookie('chat-channel-name')
}

/**
 * Remove chat channel name from cookie.
 *
 * @return {void}
 */
export function removeChatChannelName() {
  removeBrokerCookie('chat-channel-name')
}

/**
 * Gets already stored chat channel name and sets it again, to extend the session.
 *
 * @param {object} user Chat channel name will expire only if user is not logged
 * @param {boolean} isChatEmpty Chat data will expire only if chat is not empty (if there are some messages)
 * @returns {void}
 */
export function updateChatChannelName(user = {}, isChatEmpty = true) {
  const channelName = getChatChannelName()
  if (channelName) {
    setChatChannelName(channelName, user, isChatEmpty)
  }
}

/**
 * Set user redirect url
 *
 * @param {string|null} path
 * @return void
 */
export function setRedirectUrl(path = null) {
  setBrokerCookie('redirect-url', path, 1)
}

/**
 * Get user redirect url
 *
 * @return {string|null}
 */
export function getRedirectUrl() {
  return getBrokerCookie('redirect-url')
}

/**
 * Remove user redirect url
 *
 * @return {void}
 */
export function removeRedirectUrl() {
  removeBrokerCookie('redirect-url')
}

/**
 * Get time passed since timestamp
 *
 * @param timestamp Timestamp in seconds (previously created with `new Date().getTime()` for example)
 * @param outputUnit In which unit to return calculated value
 * @return {number}
 */
export function timeSinceTimestamp(timestamp = 0, outputUnit = 'seconds') {
  try {
    const toSeconds = 1000
    const toMinutes = outputUnit === 'minutes' ? 60 : 1
    return timestamp > 0
      ? Math.round((new Date().getTime() - timestamp) / toSeconds / toMinutes)
      : 0
  } catch (e) {
    return 0
  }
}

/**
 * Get stored or current document title.
 *
 * @return {string|null}
 */
function getDocumentTitle() {
  try {
    // Check if title was previously stored
    const storedDocumentTitle = Storage.getItem('originalDocumentTitle')

    // Prefer stored title over current one
    if (storedDocumentTitle) {
      return storedDocumentTitle
    }

    // Get current title if not previously stored
    return document.title
  } catch (e) {
    logError('Get document title', e)
    return null
  }
}

/**
 * Store either provided title or current document title.
 *
 * @param {string|null} title String when setting custom page title or null when saving current title (e.g. widget)
 */
export function storeDocumentTitle(title = null) {
  Storage.setItem('originalDocumentTitle', title || document.title)
}

let documentTitleTimeout = null

/**
 * Allow setting "flashing" / changing document title.
 * In a way that custom title (different for each page) is preserved.
 *
 * Need to be called whenever document title is updated, currently from:
 * <ul>
 *   <li>Header component - to be custom for each page<li>
 *   <li>Chat component - to show number of unread messages<li>
 * </ul>
 *
 * @param {string|null} updatedTitle New page title, custom for each page or null if preserving existing one and just setting "flashing" title.
 * @param {string|null} flashingTitle Changing/flashing title.
 * @param {number} interval Interval in ms by which title will be changing.
 */
export function updateDocumentTitle(updatedTitle = null, flashingTitle = null, interval = 1500) {
  const kioskDocument = getKioskDocument()
  try {
    // When called from header to set page title unique to each page
    if (updatedTitle) {
      storeDocumentTitle(updatedTitle)
    }

    if (documentTitleTimeout) {
      clearInterval(documentTitleTimeout)
    }

    if (flashingTitle) {
      if (interval > 0) {
        let i = 0
        documentTitleTimeout = setInterval(() => {
          i++
          const documentTitle = getDocumentTitle()
          if (documentTitle) {
            kioskDocument.title = i % 2 === 0 ? documentTitle : flashingTitle
          }
        }, interval)
      } else {
        kioskDocument.title = flashingTitle
      }

      Storage.setItem('hasFlashingDocumentTitle', '1')
    } else {
      const documentTitle = getDocumentTitle()
      if (documentTitle) {
        kioskDocument.title = documentTitle

        Storage.setItem('hasFlashingDocumentTitle', '0')
      }
    }
  } catch (e) {
    logError('Update document title', e)
  }
}

/**
 * Try to find staff member data based on Twilio's author.
 *
 * @param {string} attribute Which attribute to extract (e.g. 'name', 'avatar', etc.)
 * @param {string} matchType How to match "chat message author" with "staff member".
 * <ul>
 *   <li>{string} email - staffMember.email === authorIdentifier</li>
 *   <li>{number} csrDataId - staffMember.csrDataId === authorIdentifier</li>
 * </ul>
 * @param {mixed} authorIdentifier Email, ID, etc. depending on matchType
 * @param {array} staffMembers All staff members
 * @return Requested attribute or null if not found
 */
export function getStaffMemberAttribute(
  attribute,
  matchType = 'email',
  authorIdentifier,
  staffMembers,
) {
  try {
    // Look for a Pathway staff member based on Twilio's "message.author",
    // better for situation where staff member changes avatar/name but it's not yet updated in Twilio Channel
    if (!(attribute && staffMembers && staffMembers.length > 0 && authorIdentifier)) {
      return null
    }

    // Staff member with email same as email of Twilio's message author
    const foundMember = staffMembers.filter(staffMember => {
      if (matchType === 'email') {
        return (
          staffMember.hasOwnProperty('email') &&
          staffMember.email.toLowerCase() === authorIdentifier.toLowerCase()
        )
      }

      if (matchType === 'csrDataId') {
        return (
          staffMember.hasOwnProperty('csrDataId') &&
          parseInt(staffMember.csrDataId) === parseInt(authorIdentifier)
        )
      }

      return false
    })

    if (!(foundMember.length > 0 && foundMember[0])) {
      return null
    }

    // Try to find requested attribute
    if (!(foundMember[0].hasOwnProperty(attribute) && foundMember[0][attribute].length > 0)) {
      return null
    }
    return foundMember[0][attribute]
  } catch (e) {
    logMessage('Could not find staff member', e)
    return null
  }
}

export function geoLocationExists() {
  try {
    return (
      'geolocation' in navigator &&
      navigator.geolocation.__proto__.hasOwnProperty('getCurrentPosition')
    )
  } catch (e) {
    return false
  }
}

export function getSubdomain() {
  const url = window.location.hostname
  if (url.startsWith('192')) {
    return 'localhost'
  }

  const urlParts = url.split('.')

  // If without subdomain
  if (!urlParts || urlParts.length <= 2) {
    return 'no-subdomain'
  }

  return urlParts.shift()
}

/**
 * Get hash from URL.
 *
 * @param {object} location From props (when withRouter is used)
 * @param {*} defaultResponse
 * @return {string}
 */
export function getHashFromUrl(location, defaultResponse = '') {
  try {
    if (location && location.hasOwnProperty('hash') && location.hash) {
      return location.hash
    }
    return defaultResponse
  } catch (e) {
    return defaultResponse
  }
}

/**
 * Extract param from URL which is not placeholder "0" param.
 *
 * @param {object} params From router: props.match.params
 * @param {string} paramName
 * @param {*} defaultResponse
 * @return {string|*}
 */
export function getParamFromUrl(params, paramName, defaultResponse = '') {
  try {
    if (
      isEmpty(params) ||
      !params.hasOwnProperty(paramName) ||
      !params[paramName] ||
      params[paramName] === '0'
    ) {
      return defaultResponse
    }

    return params[paramName]
  } catch (e) {
    return defaultResponse
  }
}

/**
 * Extract param from URL without react-router help which is not placeholder "0" param.
 *
 * For example when used from App component which isn't a router.
 *
 * @param {object} location Basically props.location
 * @param {int} paramPosition Which param is this in URL (
 *  e.g. for getting "ref" in this url /auth/email/:token/:ref/:redirectTo, we pass: 4
 * @param {*} defaultResponse
 * @return {string|*}
 */
export function getParamFromUrlWithoutRouter(location, paramPosition, defaultResponse = '') {
  try {
    if (
      !(!isEmpty(location) && location.hasOwnProperty('pathname') && !isEmpty(location.pathname))
    ) {
      return defaultResponse
    }

    const pathParams = location.pathname.split('/')

    // Exit if params are empty or we can't find param at this position
    if (!(!isEmpty(pathParams) && pathParams.hasOwnProperty(paramPosition))) {
      return defaultResponse
    }

    const value = pathParams[paramPosition]

    // Do not return 0, but default response
    if (value === '0') {
      return defaultResponse
    }

    return value
  } catch (e) {
    return defaultResponse
  }
}

/**
 * Try to extract state from router location.
 *
 * @param {object} location Basically props.location
 * @param {string} stateKey Which state to get
 * @param {*} defaultResponse
 * @return {string|*}
 */
export function getRouterLocationState(location, stateKey, defaultResponse = null) {
  try {
    if (
      !isEmpty(location) &&
      location.hasOwnProperty('state') &&
      location.state.hasOwnProperty(stateKey) &&
      !isEmpty(location.state[stateKey])
    ) {
      return location.state[stateKey]
    }

    return defaultResponse
  } catch (e) {
    return defaultResponse
  }
}

/**
 * Checks if array or object is empty or not.
 *
 * @param {array|object} value
 * @return {boolean}
 */
export function isEmpty(value) {
  try {
    if (!value) {
      return true
    }

    if (Array.isArray(value) === true && value.length <= 0) {
      return true
    }

    return Object.keys(value).length === 0 && value.constructor === Object
  } catch (e) {
    return true
  }
}

// Detects if device is on iOS
export function isIos() {
  const userAgent = window.navigator.userAgent.toLowerCase()
  return /iphone|ipad|ipod/.test(userAgent)
}

// Detects if device is macOS
export function isMacOs() {
  return navigator.platform.indexOf('Mac') > -1
}

// Detects if device is on IE
export function isIE() {
  return /* @cc_on!@*/ !!document.documentMode
}

// Detects if device is on Edge browser
export function isEdge() {
  return window.navigator.userAgent.indexOf('Edge/') > 0
}

export function isFirefox() {
  return navigator.userAgent.toLowerCase().indexOf('firefox') > -1
}

// Detects if device is in standalone mode
export function isInStandaloneMode() {
  if (window.navigator.standalone) {
    return true
  }
  if (navigator.standalone) {
    return true
  }
  if (document.referrer.includes('android-app://')) {
    return true
  }
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true
  }
  if (matchMedia('(display-mode: standalone)').matches) {
    return true
  }
  return false
}

/**
 * Detect if current device supports touch.
 *
 * @return {boolean}
 */
export function isTouchDevice() {
  try {
    return !!(
      ('ontouchstart' in window || navigator.maxTouchPoints) // works on most browsers // works on IE10/11 and Surface
    )
  } catch (e) {
    return false
  }
}

export function camelize(str) {
  return str
    .replace('_', ' ')
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function(letter) {
      return letter.toUpperCase()
    })
    .replace(/\s+/g, '')
}

export function safelyParseJSON(json) {
  // This function cannot be optimised, it's best to
  // keep it small!
  let parsed

  try {
    parsed = JSON.parse(json)
  } catch (e) {
    // Oh well, but whatever...
  }

  return parsed // Could be undefined!
}

export function hasLocalStorage() {
  const test = 'temporary-ls-test'
  try {
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Gets chatLogLevel from localStorage or false if not set
 */
export function getChatLogLevel() {
  // Allow setting 'chatLogLevel' from local storage (to enable Twilio debug just while testing), e.g. 'debug'
  if (hasLocalStorage() && localStorage.getItem('chatLogLevel')) {
    return localStorage.getItem('chatLogLevel')
  } else {
    return false
  }
}

/**
 * Tries to get iframe's `window` for widget mode or just returns global `window` as a fallback.
 *
 * // TODO: PAT-2954 Check if using frameWindow from widget context is better
 *
 * @return {Window}
 */
export function getKioskWindow() {
  try {
    const iframeContainer = document.querySelector('#kiosk-container')

    if (iframeContainer && iframeContainer.contentWindow) {
      return iframeContainer.contentWindow
    }

    return window
  } catch (e) {
    return window
  }
}

/**
 * Tries to get iframe's `document` for widget mode or just returns global `document` as a fallback.
 *
 * // TODO: PAT-2954 Check if using frameDocument from widget context is better
 *
 * @return {Document}
 */
export function getKioskDocument() {
  try {
    const iframeContainer = document.querySelector('#kiosk-container')

    if (iframeContainer) {
      if (iframeContainer.contentDocument) {
        return iframeContainer.contentDocument
      }

      if (iframeContainer.contentWindow && iframeContainer.contentWindow.document) {
        return iframeContainer.contentWindow.document
      }
    }

    return document
  } catch (e) {
    return document
  }
}

export function getBodyContainer() {
  let getDocument = getKioskDocument()

  try {
    const container = getDocument.querySelector('#kiosk-body-container')

    if (container) {
      return container
    }

    return getDocument.body
  } catch (e) {
    return getDocument.body
  }
}

/**
 * Extracts information about current page from location object
 *
 * @param {object} location Should be available in props and coming from router (react router)
 * @param {string} info
 * @return {string}
 */
export function getCurrentPageUrlInfo(location, info = '') {
  try {
    if (!location) {
      return ''
    }

    // Location should be in the context of app (main document for pwa/desktop or iframe for widget)
    const currentPath = location.pathname ? location.pathname : ''
    const currentPageCls =
      currentPath !== '/' ? currentPath.replace('/', '').replace(/\//g, ' ') : 'home'

    if (info === 'url') {
      // Current page URL (usage example is setting main menu items as active)
      return currentPath
    }

    if (info === 'cls') {
      // Current page CSS class (used for setting custom and unique CSS class for each page)
      return currentPageCls
    }

    return ''
  } catch (e) {
    logError('Get current page info', e)
    return ''
  }
}

/**
 * Takes 12 hour format (AM/PM) and returns Date object with provided time
 */
const convertTimeStringToObject = time12h => {
  // Extract time and AM/PM
  const [time, modifier] = time12h.split(' ')

  // Extract hours and minutes form time
  let [hours, minutes] = time.split(':')

  if (hours === '12') {
    hours = '00'
  }

  // If PM, convert hour value (e.g. 11 to 23)
  if (modifier.toLowerCase() === 'pm') {
    hours = parseInt(hours, 10) + 12
  }

  // Take current date and time
  let now = new Date()

  // Set hours and minutes
  now.setHours(hours, minutes)

  // Return todays date with provided AM/PM time
  return now
}

/**
 * Takes current client time and applies provided time zone offset
 *
 * @param {number} timeZoneOffsetInHours
 */
const getNowAdjustedForTimeZone = timeZoneOffsetInHours => {
  // Get current date time
  const now = new Date()

  // Return just current date time if we didn't get time zone from settings
  if (!timeZoneOffsetInHours) {
    return now
  }

  // Get client's time zone offset
  const clientTimeZoneOffset = now.getTimezoneOffset()

  // Get the timezone offset from local time in minutes
  const tzDifferenceInMinutes = timeZoneOffsetInHours * 60 + clientTimeZoneOffset

  // Convert the offset to milliseconds, add to now, and make a new Date
  return new Date(now.getTime() + tzDifferenceInMinutes * 60 * 1000)
}

/**
 * Get current day considering timezone offset.
 *
 * @param {number} timeZoneOffsetInHours
 * @return {null|string} mon-sun
 */
export function getDayOfWeek(timeZoneOffsetInHours) {
  if (!timeZoneOffsetInHours) {
    return null
  }

  const nowWithTimeZone = getNowAdjustedForTimeZone(timeZoneOffsetInHours)

  const day = nowWithTimeZone.getDay()

  if (day === 0) {
    return 'sun'
  }
  if (day === 1) {
    return 'mon'
  }
  if (day === 2) {
    return 'tue'
  }
  if (day === 3) {
    return 'wed'
  }
  if (day === 4) {
    return 'thu'
  }
  if (day === 5) {
    return 'fri'
  }
  if (day === 6) {
    return 'sat'
  }
}

/**
 * Check if current day and time is outside working hours.
 *
 * @param {object} afterHours
 * @param {number} timeZoneOffsetInHours
 * @return {boolean}
 */
export function isAfterHours(afterHours, timeZoneOffsetInHours) {
  try {
    const { daysAvailable, startTime, endTime } = afterHours

    const nowWithTimeZone = getNowAdjustedForTimeZone(timeZoneOffsetInHours)

    // Convert sunday from 0 to 7
    const nowDayOfWeek = nowWithTimeZone.getDay() === 0 ? 7 : nowWithTimeZone.getDay()

    // If current day of the week is not in provided working days
    if (!daysAvailable.includes(nowDayOfWeek.toString())) {
      return true
    }

    // Convert AM/PM time string to Date object
    const workStart = convertTimeStringToObject(startTime)
    const workEnd = convertTimeStringToObject(endTime)

    // If time is within working hours
    const withinWorkingHours = nowWithTimeZone > workStart && nowWithTimeZone < workEnd

    // Show logs if "debugIsAfterHours" cookie is set
    if (hasLocalStorage() && localStorage.getItem('debugIsAfterHours')) {
      const now = new Date()
      console.log('office timeZone:      ', timeZoneOffsetInHours)
      console.log('now.getTimezoneOffset:', now.getTimezoneOffset())
      console.log('now:                  ', now)
      console.log('nowWithTimeZone:      ', nowWithTimeZone)
      console.log('workStart:            ', workStart)
      console.log('workEnd               ', workEnd)
      console.log('nowDayOfWeek          ', nowDayOfWeek)
      console.log('withinWorkingHours:   ', withinWorkingHours ? 'Yes' : 'No')
    }

    // Return opposite of time within working hours (a.k.a. after hours)
    return !withinWorkingHours
  } catch (e) {
    return false
  }
}

/**
 * Extract error message from caught error
 *
 * @param {object} caughtError
 * @param {string} defaultMessage Default message to show if error message not found
 *
 * @returns {string}
 */
export function getErrorMessage(caughtError, defaultMessage = '') {
  try {
    if (caughtError && caughtError.response && caughtError.response.data) {
      if (caughtError.response.data.error) {
        return caughtError.response.data.error
      }
      if (caughtError.response.data._info && caughtError.response.data._info.error) {
        return caughtError.response.data._info.error
      }
    }

    if (caughtError && caughtError.message) {
      return caughtError.message
    }

    return defaultMessage
  } catch (e) {
    return defaultMessage
  }
}

/**
 * Extract error code (status) from caught error
 *
 * e.g. 401
 *
 * @param {object} caughtError
 * @param {int} defaultResponse Default response if code was not found
 *
 * @returns {int|mixed}
 */
export function getErrorCode(caughtError, defaultResponse = 400) {
  try {
    if (caughtError.response.data.code) {
      return parseInt(caughtError.response.data.code)
    }
    return defaultResponse
  } catch (e) {
    return defaultResponse
  }
}

/**
 * Extract error message from redux reducer
 *
 * @param {object} action Redux action
 *
 * @returns {string|null}
 */
export function getReducerErrorMessage(action) {
  // Try to find error message
  try {
    if (action.payload.response.data.error.length > 0) {
      return action.payload.response.data.error
    }
  } catch (dataError) {
    try {
      if (action.payload.response.data._info.error.length > 0) {
        return action.payload.response.data._info.error
      }
    } catch (infoError) {
      try {
        if (action.payload.length > 0) {
          return action.payload.toString()
        }
      } catch (e) {
        return null
      }
    }
  }

  return null
}

/**
 * Extract response data from (failed) redux reducer
 *
 * @param {object} action Redux action
 *
 * @returns {string|null}
 */
export function getReducerResponsePayload(action) {
  try {
    return action.payload.response.data.data ? action.payload.response.data.data : null
  } catch (e) {
    return null
  }
}

/**
 * Try to extract required settings in a way where errors are handled.
 *
 * @param {object} state Whole redux state
 * @param {string} property Which property to find from settings
 * @param {any} defaultValue Default value to return if property not found
 * @return {null|any}
 */
export function getFromSettings(state, property, defaultValue = null) {
  try {
    if (!state || !state.hasOwnProperty('appReducer') || !state.appReducer) {
      return defaultValue
    }
    if (
      !state.appReducer.hasOwnProperty('settings') ||
      !state.appReducer.settings.hasOwnProperty(property)
    ) {
      return defaultValue
    }

    return state.appReducer.settings[property]
  } catch (e) {
    // TODO: PAT-1770 If this creates too much noise in Sentry, pass "1" as 3rd param to disable sending to Sentry
    logError(`Could not get "${property}" from state.appReducer.settings`, {
      error: e,
      state: state,
    })
    return defaultValue
  }
}

/**
 * Try to extract required settings in a way where errors are handled.
 *
 * @param {object} state Whole redux state
 * @param {string} property Which property to find from features
 * @param {string} key Which property to find from feature fiund from
 * @param {any} defaultValue Default value to return if property not found
 * @return {null|any}
 */
export function getFromFeatures(state, property, key, defaultValue = null) {
  try {
    if (!state || !state.hasOwnProperty('appReducer') || !state.appReducer) {
      return defaultValue
    }
    if (!state.appReducer.hasOwnProperty('features')) {
      return defaultValue
    }

    const existingFeature = state.appReducer.features.find(feature => feature.id === property)
    return existingFeature[key]
  } catch (e) {
    // TODO: PAT-1770 If this creates too much noise in Sentry, pass "1" as 3rd param to disable sending to Sentry
    logError(`Could not get "${property}" from state.appReducer.features`, {
      error: e,
      state: state,
    })
    return defaultValue
  }
}

/**
 * Log errors to console log and also pass to Sentry
 *
 * @param {string} message Short error description
 * @param {string|object|null} error Can be empty when we know just error message
 * @param {int} level Level of importance where higher level logs stand our more and minimum level for passing to Sentry is 2
 *
 * @returns {void}
 */
export function logError(message = '', error = null, level = 10) {
  // If error is not passed, create new one to get stack trace and other useful info
  const getError = error || new Error('Kiosk unknown')

  // Format error description
  const errorDescription = message ? ' ' + message : ''

  // Add big red icon to find error in console easier
  const standOutIcon = level > 5 ? '❌' : ''

  // Simple console log
  console.error(`${standOutIcon}[Kiosk]${errorDescription}\n`, getError)

  // Pass to Sentry unless level is really low so it's not that important
  if (level > 1 || (hasLocalStorage() && localStorage.getItem('pushKioskLogsToSentry'))) {
    Sentry.captureException(getError)
  }
}

/**
 * Output messages to simple console.log and also to Sentry
 *
 * @param {string} message Message to show
 * @param {string|object|null} data Additional data to show
 * @param {int} level Level of importance where minimum level for passing to Sentry is 2
 *
 * @returns {void}
 */
export function logMessage(message = '', data = null, level = 1) {
  console.log(`[Kiosk] ${message}`, data || '')

  if (level > 1 || (hasLocalStorage() && localStorage.getItem('pushKioskLogsToSentry'))) {
    Sentry.captureMessage(message)
  }
}

/**
 * Get log level of importance from error.
 *
 * @param {object} error Caught error
 * @returns {int}
 */
export function getLogLevel(error) {
  // Do not report to Sentry if there is no internet connection (Network Error)
  if (error && error.hasOwnProperty('message') && error.message === 'Network Error') {
    return 1
  }

  return 10
}

export function hasFeatureFlag(enabledFeatureFlags = [], feature = '') {
  try {
    // Enable all feature flags for local development
    if (isLocal() || isNodeLocal()) {
      return true
    }
    return enabledFeatureFlags && enabledFeatureFlags.includes(feature)
  } catch (e) {
    return false
  }
}

export function getAddressFromUser(user = {}) {
  const { address, city, province, zip, state } = user

  let addressArr = []
  if (address) {
    addressArr.push(address)
  }
  if (city) {
    addressArr.push(city)
  }
  if (province) {
    addressArr.push(province)
  }
  if (zip) {
    addressArr.push(zip)
  }
  if (state) {
    addressArr.push(state)
  }

  return addressArr.length > 0 ? addressArr.join(', ') : ''
}

/**
 * Format location selected from google map to displayable string.
 *
 * @param location Google map location data or simple TextArea value
 * @param defaultValue
 * @return {string}
 */
export function getAddressFromLocation(location = {}, defaultValue = '') {
  // Needed for single TextArea value
  if (location.hasOwnProperty('locationText') && location.locationText) {
    return location.locationText
  }

  // Needed for location from map
  const { streetName, streetNumber, city, province, country } = location
  if (streetName && city && province && country) {
    if (streetNumber) {
      return `${streetNumber} ${streetName} ${city}, ${province}, ${country}`
    } else {
      return `${streetName}, ${city}, ${province}, ${country}`
    }
  } else if (streetName && province && country) {
    return `${streetName}, ${province}, ${country}`
  } else if (city && province && country) {
    return `${city}, ${province}, ${country}`
  } else if (city && country) {
    return `${city}, ${country}`
  } else if (country) {
    return `${country}`
  }

  return defaultValue
}

export function getIconFromGraphics(graphics, iconName = 'favicon') {
  try {
    const icon = graphics.icons.find(i => i.name === iconName)
    return icon && icon.src ? icon.src : ''
  } catch (e) {
    return ''
  }
}

export const getIconColor = () => {
  const doc = getKioskDocument()
  return getComputedStyle(doc.documentElement).getPropertyValue('--chakra-colors-card-font')
}

/**
 * Request settings from backend.
 *
 * Keep in mind that neither Accept-Language or Selected-Language headers are set
 * for initial widget request (referer = 'w'). But that's why we only get colors in that request.
 *
 * @param {string|null} referer When are we requesting brand settings
 * <ul>
 *   <li>"i-a" Kiosk Init - App</li>
 *   <li>"i-w" Kiosk Init - Widget</li>
 *   <li>"c" Kiosk Core (load or page reload) only graphics</li>
 *   <li>"w" Kiosk Widget (load or page reload) only brand colors</li>
 *   <li>"a" Kiosk Auth - after successful log in</li>
 *   <li>"l" Kiosk Language - after language is changed</li>
 *   <li>"m" Disclaimer, After Hours, and other custom messages</li>
 * </ul>
 * @return {Promise<object>}
 */
export async function getBrandSettings(referer = null) {
  return await getRequest({
    base: '/brand/getKioskBrandSettings',
    query: [
      {
        name: 'rf',
        value: referer,
      },
    ],
  }).catch(e => {
    logError('Get brand settings', e, getLogLevel(e))
    return {
      error: e,
    }
  })
}

/**
 * Request features from backend.
 *
 * @return {Promise<object>}
 */
export async function getFeatures() {
  return await getRequest({
    base: '/brand/features',
  }).catch(e => {
    logError('Get features', e, getLogLevel(e))
    return {
      error: e,
    }
  })
}

/**
 * Capitalize string helper
 *
 * @return {String}
 */
export const capitalize = str => {
  const arr = str.split(' ')

  for (var i = 0; i < arr.length; i++) {
    arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1)
  }

  const capitalizedStr = arr.join(' ')

  return capitalizedStr
}

/**
 * Convert url string to page class
 *
 * @return {String}
 */
export const getPageClassFromUrl = str => {
  if (str.length === 0) {
    return str
  }

  // eslint-disable-next-line no-useless-escape
  const classSuffix = str
    .replace(/\W+/g, '-')
    // eslint-disable-next-line no-useless-escape
    .replace(/\-$/, '')
    .toLowerCase()

  return `page${classSuffix}`
}

/**
 * Check whether a hex color is white
 *
 * @param color
 * @returns {boolean}
 */
export const isColorWhite = color => {
  if (color.length === 0) {
    return false
  }
  return ['#fff', '#FFF', '#ffffff', '#FFFFFF'].includes(color)
}

/**
 * invoke callback when enter ot space button pressed
 *
 * @param cb
 * @returns {void}
 */
export const handleKeyDown = cb => {
  return e => {
    const spacePressed = e.key == ' ' || e.code == 'Space' || e.keyCode == 32
    const keyDown = e.key !== undefined ? e.key : e.keyCode
    if (keyDown === 'Enter' || keyDown === 13 || spacePressed) {
      cb(e)
    }
  }
}

/**
 * Return true if a client is a mobile device
 *
 * @return {Boolean}
 */
export const isMobileDevice = () => {
  const details = window.navigator.userAgent
  const regexp = /android|iphone|kindle|ipad/i
  return regexp.test(details)
}
